import { useState } from 'react'
import { Plus, UserPlus } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import {
  buildCreateUserPayload,
  describeUserFormGap,
  emptyUserForm,
  type UserFormValues,
} from '@/lib/api/user-payload'
import type { CreateUserRequest, MetadataData } from '@/lib/api/user-management.types'
import { UserFormFields } from './UserFormFields'

interface AddUserModalProps {
  onClose: () => void
  onCreate: (payload: CreateUserRequest) => void
  metadata: MetadataData
  /** True while create-user is in flight. */
  pending?: boolean
}

/**
 * Provisioning form for a brand-new system user.
 *
 * Hands its parent a finished create-user payload rather than raw form state, so
 * the role-driven scope rules are applied in exactly one place. There is no
 * password field: the backend generates the initial one and the account comes back
 * with `must_change_password: true`.
 */
export function AddUserModal({ onClose, onCreate, metadata, pending = false }: AddUserModalProps) {
  const [form, setForm] = useState<UserFormValues>(emptyUserForm)

  const patch = (next: Partial<UserFormValues>) => setForm((f) => ({ ...f, ...next }))
  const gap = describeUserFormGap(form)

  const submit = () => {
    // Nothing leaves this dialog while a field is invalid. The button is disabled
    // too; this is the guard for every other way a click could arrive.
    if (gap || pending) return
    onCreate(buildCreateUserPayload(form))
    // Deliberately NOT cleared here. The request is still in flight, and clearing
    // now would both blank the fields under the admin's cursor (showing "enter a
    // name" against a user who is being created) and destroy everything they typed
    // if the create comes back 409 or 400. The parent unmounts this dialog on
    // success, which is what resets it.
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add New User"
      className="max-w-2xl"
      icon={<UserPlus className="text-primary-bright mt-0.5 size-5 shrink-0" />}
      footer={
        <Button variant="cta" onClick={submit} disabled={Boolean(gap) || pending}>
          <Plus className="size-4" />
          {pending ? 'Creating…' : 'Create User'}
        </Button>
      }
    >
      {/* The form owns its own validation messages — per field, plus one summary
          line for whatever has no inline message yet. `gap` here only gates the
          submit button. */}
      <UserFormFields form={form} onChange={patch} metadata={metadata} variant="create" />

      {/* `mt-5` and an even `p-3` keep the note on the form's own spacing scale —
          20px between blocks, 12px inside a box — instead of 24px / 14px-by-12px. */}
      <p className="border-line-bright/70 text-fg-muted mt-5 rounded-lg border border-dashed p-3 text-[13px] leading-relaxed">
        <span className="text-primary-bright font-semibold">NOTE:</span> The account is created with
        a temporary password and must be changed on first sign-in. Outside production no welcome
        email is sent, so the password has to be shared directly.
      </p>
    </Modal>
  )
}
